import { t } from 'i18next';
import { v4 as uuidv4 } from 'uuid';

import { MessageRole, type Message } from 'features/chat/types/chat';
import { formatShowMessageTimeForSend } from 'features/chat/utils/message-show-time';
import { hasAddressForms } from 'features/conversation-preferences/address-form';
import {
  isAcceptedPreferenceText,
  type AddressForm,
  type IndustryCode,
} from 'features/conversation-preferences/services/conversation-preferences-service';
import { initialConversationState, useChatStore } from 'state/store/chat/use-chat-store';
import { useCommonStore } from 'state/store/common/useCommonStore';
import {
  useOnboardingStore,
  type OnboardingProgress,
} from 'state/store/onboarding/use-onboarding-store';

import {
  ONBOARDING_ADDRESS_OPTIONS,
  ONBOARDING_CONVERSATION_PREFIX,
  ONBOARDING_TEAM_OPTIONS,
  ONBOARDING_TOOL_OPTIONS,
  isOnboardingConversationId,
  onboardingAnswerRetry,
  onboardingAnswerUnrelated,
  onboardingCatchUpIntro,
  onboardingIndustryCustomPrompt,
  onboardingIndustryQuestion,
  onboardingLanguage,
  onboardingNameQuestion,
  onboardingSalutationCustomPrompt,
  onboardingSalutationQuestion,
  onboardingTeamQuestion,
  onboardingToolsAcknowledged,
  onboardingToolsQuestion,
} from './onboarding-script';
import {
  ONBOARDING_AREA_STEP,
  ONBOARDING_INDUSTRY_STEP,
  ONBOARDING_SALUTATION_STEP,
  ONBOARDING_TOOLS_STEP,
  onboardingCatchUpStep,
  type OnboardingAnswers,
} from './onboarding-journey';
import { extractOnboardingAnswer } from './onboarding-answer';
import { ONBOARDING_INDUSTRY_OPTIONS, onboardingIndustryLabel } from './onboarding-industry';
import {
  ONBOARDING_SALUTATION_OPTIONS,
  needsSalutationChoice,
  onboardingSalutationName,
  parseSalutationText,
} from './onboarding-salutation';
import {
  completeOnboardingPreferences,
  industryPreferences,
  saveOnboardingPreferences,
  salutationPreferences,
} from './onboarding-preferences';

export { isOnboardingConversationId };

const THINKING_DELAY_MS = 1000;
const CHUNK_INTERVAL_MS = 38;
const CHUNK_CHARS = 2;

type SetOnboardingStep = (next: number, answers?: OnboardingAnswers) => void;

type ActiveOnboardingStream = {
  conversationId: string;
  messageId: string;
  fullText: string;
  thinkingTimer: ReturnType<typeof setTimeout> | null;
  chunkTimer: ReturnType<typeof setInterval> | null;
};

let activeOnboardingStream: ActiveOnboardingStream | null = null;

export function createOnboardingConversationId(): string {
  return `${ONBOARDING_CONVERSATION_PREFIX}${uuidv4()}`;
}

function createOnboardingMessage(
  role: MessageRole,
  content: string,
  isThisGenerationOnGoing = false
): Message {
  const id = uuidv4();
  return {
    recordId: id,
    role,
    senderId: role === MessageRole.USER ? useCommonStore.getState().user?.id : undefined,
    generations: [
      {
        id,
        content,
        createdDate: Date.now(),
        showMessageTime: formatShowMessageTimeForSend(),
        ...(isThisGenerationOnGoing ? { isThisGenerationOnGoing: true } : {}),
      },
    ],
  };
}

function writeOnboardingConversation(conversationId: string, messages: Message[]) {
  const conversation = initialConversationState(
    conversationId,
    t('ONBOARDING', { defaultValue: 'Onboarding' })
  );
  conversation.messages = messages;
  conversation.hasFetchedInitialMessages = true;
  conversation.isLoading = false;

  useChatStore.setState((state) => ({
    activeConversationId: conversationId,
    conversations: {
      ...state.conversations,
      [conversationId]: conversation,
    },
  }));
}

const readProgress = (): OnboardingProgress => {
  const userId = useCommonStore.getState().user?.id;
  return (userId && useOnboardingStore.getState().progressByUser[userId]) || {};
};

/** The saved answers as the progress fields the lines are built from. */
const progressFromAnswers = (answers: OnboardingAnswers): OnboardingProgress => ({
  ...answers,
  name: answers.onboardingName,
  team: answers.onboardingTeam,
  tools: answers.onboardingTools,
  completed: answers.onboardingCompleted,
});

/** The industry question, addressing the user by name or salutation in their form. */
const industryQuestionFor = (progress: OnboardingProgress) =>
  onboardingIndustryQuestion({
    form: progress.addressForm,
    name: progress.givenName ?? progress.name,
    salutationName: onboardingSalutationName(progress),
  });

/** Buddy's current question for a step: hydrates a reload, repeats after an unrelated answer
 *  and re-renders after a language switch. */
export function onboardingQuestionFor(step: number, progress: OnboardingProgress): string {
  const form = progress.addressForm;
  if (step === ONBOARDING_SALUTATION_STEP) {
    return progress.catchUp && !form && hasAddressForms(onboardingLanguage())
      ? onboardingCatchUpIntro()
      : onboardingSalutationQuestion();
  }
  if (step === ONBOARDING_INDUSTRY_STEP) return industryQuestionFor(progress);
  if (step === ONBOARDING_AREA_STEP) {
    return onboardingTeamQuestion(
      onboardingIndustryLabel(progress.industry, progress.industryText),
      form
    );
  }
  if (progress.completed && progress.tools?.trim()) {
    return onboardingToolsAcknowledged(progress.tools, form);
  }
  return onboardingToolsQuestion(progress.team, form);
}

/** The catch-up intro ends by asking about du (appendix), so it is only said while the form is
 *  unknown, or in English, where it asks nothing. */
const showsCatchUpIntro = (progress: OnboardingProgress) =>
  !progress.addressForm || !hasAddressForms(onboardingLanguage());

/** The reply after the name: the salutation question when the name left it open, else the
 *  industry question. */
const nameReply = (progress: OnboardingProgress) =>
  needsSalutationChoice(progress) ? onboardingSalutationQuestion() : industryQuestionFor(progress);

export function startOnboardingConversation(
  name: string,
  response = name,
  answers: OnboardingAnswers = {}
): string {
  const conversationId = createOnboardingConversationId();
  writeOnboardingConversation(conversationId, [
    createOnboardingMessage(MessageRole.ASSISTANT, onboardingNameQuestion()),
    createOnboardingMessage(MessageRole.USER, response.trim()),
  ]);
  streamOnboardingAssistantReply(
    conversationId,
    nameReply({ ...progressFromAnswers(answers), name: name.trim() })
  );
  return conversationId;
}

export function appendOnboardingMessage(
  conversationId: string,
  role: MessageRole,
  content: string
) {
  useChatStore
    .getState()
    ._addMessageToActiveConversation(
      createOnboardingMessage(role, content),
      undefined,
      conversationId
    );
}

function patchOnboardingAssistantMessage(
  conversationId: string,
  messageId: string,
  content: string,
  isOngoing: boolean
) {
  useChatStore.setState((state) => {
    const conversation = state.conversations[conversationId];
    if (!conversation) return {};

    return {
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...conversation,
          isCurrentResponseGenerating: isOngoing,
          messages: conversation.messages.map((message) => {
            if (message.recordId !== messageId) return message;
            return {
              ...message,
              generations: message.generations.map((generation, index) =>
                index === 0
                  ? {
                      ...generation,
                      content,
                      isThisGenerationOnGoing: isOngoing,
                    }
                  : generation
              ),
            };
          }),
        },
      },
    };
  });
}

function clearOnboardingStreamTimers(stream: ActiveOnboardingStream) {
  if (stream.thinkingTimer) clearTimeout(stream.thinkingTimer);
  if (stream.chunkTimer) clearInterval(stream.chunkTimer);
  stream.thinkingTimer = null;
  stream.chunkTimer = null;
}

/** Finish or drop any in-flight fake stream so a new answer can start cleanly. */
function cancelOnboardingAssistantStream(completeText = true) {
  const stream = activeOnboardingStream;
  if (!stream) return;

  clearOnboardingStreamTimers(stream);
  activeOnboardingStream = null;
  if (completeText) {
    patchOnboardingAssistantMessage(
      stream.conversationId,
      stream.messageId,
      stream.fullText,
      false
    );
  }
}

/** Empty assistant bubble → short thinking pause → typewriter of scripted text. */
function streamOnboardingAssistantReply(conversationId: string, fullText: string) {
  cancelOnboardingAssistantStream(true);

  const message = createOnboardingMessage(MessageRole.ASSISTANT, '', true);
  const messageId = message.recordId;

  useChatStore.getState()._addMessageToActiveConversation(message, undefined, conversationId);
  useChatStore.setState((state) => {
    const conversation = state.conversations[conversationId];
    if (!conversation) return {};
    return {
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...conversation,
          isCurrentResponseGenerating: true,
        },
      },
    };
  });

  const stream: ActiveOnboardingStream = {
    conversationId,
    messageId,
    fullText,
    thinkingTimer: null,
    chunkTimer: null,
  };
  activeOnboardingStream = stream;

  stream.thinkingTimer = setTimeout(() => {
    stream.thinkingTimer = null;
    let index = 0;

    stream.chunkTimer = setInterval(() => {
      index = Math.min(fullText.length, index + CHUNK_CHARS);
      const next = fullText.slice(0, index);
      const done = index >= fullText.length;
      patchOnboardingAssistantMessage(conversationId, messageId, next, !done);

      if (done) {
        clearOnboardingStreamTimers(stream);
        if (activeOnboardingStream === stream) activeOnboardingStream = null;
      }
    }, CHUNK_INTERVAL_MS);
  }, THINKING_DELAY_MS);
}

/** After a language switch: Buddy's last line becomes the current question in the new
 *  language. Answers are untouched; a line still being typed is left alone. */
export function relocalizeOnboardingQuestion(conversationId: string, question: string) {
  if (activeOnboardingStream?.conversationId === conversationId) return;
  const conversation = useChatStore.getState().conversations[conversationId];
  if (!conversation || conversation.isCurrentResponseGenerating) return;
  const last = conversation.messages.at(-1);
  if (!last || last.role !== MessageRole.ASSISTANT || last.generations[0]?.content === question)
    return;
  patchOnboardingAssistantMessage(conversationId, last.recordId, question, false);
}

/** Rebuild the local transcript after a reload from saved answers. */
export function hydrateOnboardingConversation(conversationId: string, answers: OnboardingAnswers) {
  if (!isOnboardingConversationId(conversationId)) return;

  const existing = useChatStore.getState().conversations[conversationId];
  if (existing?.messages?.length) {
    if (useChatStore.getState().activeConversationId !== conversationId) {
      useChatStore.getState().setActiveConversationId(conversationId);
    }
    return;
  }

  const progress = progressFromAnswers(answers);
  const messages = progress.catchUp ? catchUpTranscript(progress) : journeyTranscript(progress);
  writeOnboardingConversation(conversationId, messages);
}

/** Collects transcript lines in order: Buddy's (`assistant`) and the user's answers (`user`). */
function transcriptBuilder() {
  const messages: Message[] = [];
  return {
    messages,
    assistant: (content: string) =>
      messages.push(createOnboardingMessage(MessageRole.ASSISTANT, content)),
    user: (content: string) => messages.push(createOnboardingMessage(MessageRole.USER, content)),
  };
}

/** The catch-up: its intro, then the salutation question or the industry with its answer. */
function catchUpTranscript(progress: OnboardingProgress): Message[] {
  const { messages, assistant, user } = transcriptBuilder();
  const industry = onboardingIndustryLabel(progress.industry, progress.industryText);
  if (showsCatchUpIntro(progress)) assistant(onboardingCatchUpIntro());
  if (onboardingCatchUpStep(progress) === ONBOARDING_SALUTATION_STEP) {
    if (progress.addressForm) assistant(onboardingSalutationQuestion());
  } else {
    assistant(industryQuestionFor(progress));
    if (industry) user(industry);
  }
  return messages;
}

/** Adds the journey up to the first unanswered step: name, industry, area and tools. */
function addJourneySteps(
  progress: OnboardingProgress,
  { assistant, user }: Pick<ReturnType<typeof transcriptBuilder>, 'assistant' | 'user'>
) {
  const name = progress.name?.trim();
  if (!name) return;
  assistant(onboardingNameQuestion());
  user(name);
  assistant(nameReply(progress));

  const industry = onboardingIndustryLabel(progress.industry, progress.industryText);
  if (needsSalutationChoice(progress) || !industry) return;
  user(industry);
  assistant(onboardingTeamQuestion(industry, progress.addressForm));

  const team = progress.team?.trim();
  if (!team) return;
  user(team);
  assistant(onboardingToolsQuestion(team, progress.addressForm));

  const tools = progress.tools?.trim();
  if (tools) {
    user(tools);
    assistant(onboardingToolsAcknowledged(tools, progress.addressForm));
  }
}

/** The journey up to the first unanswered step. */
function journeyTranscript(progress: OnboardingProgress): Message[] {
  const builder = transcriptBuilder();
  addJourneySteps(progress, builder);
  return builder.messages;
}

const pendingAnswers = new Map<string, object>();

/** Invalidate a result when the user leaves this question before extraction finishes. */
export function cancelPendingOnboardingAnswer(conversationId: string) {
  if (!pendingAnswers.delete(conversationId)) return;
  useChatStore.setState((state) => {
    const conversation = state.conversations[conversationId];
    if (!conversation) return {};
    return {
      conversations: {
        ...state.conversations,
        [conversationId]: { ...conversation, isCurrentResponseGenerating: false },
      },
    };
  });
}

const optionLabels = (options: readonly { key: string; defaultValue: string }[]) =>
  options.map(({ key, defaultValue }) => t(key, { defaultValue }));

/** Starts an answer: shows it, blocks chips and composer, and returns a token that stays valid
 *  until the user leaves the question. Null when the answer can't be taken now. */
function beginOnboardingAnswer(conversationId: string, text: string) {
  if (
    !text ||
    !isOnboardingConversationId(conversationId) ||
    pendingAnswers.has(conversationId) ||
    !useChatStore.getState().conversations[conversationId]
  )
    return null;
  cancelOnboardingAssistantStream(true);
  appendOnboardingMessage(conversationId, MessageRole.USER, text);
  const token = {};
  pendingAnswers.set(conversationId, token);
  // Use the existing chat loading flag so both chips and composer are blocked.
  useChatStore.setState((state) => ({
    conversations: {
      ...state.conversations,
      [conversationId]: {
        ...state.conversations[conversationId],
        isCurrentResponseGenerating: true,
      },
    },
  }));
  return { token, userId: useCommonStore.getState().user?.id };
}

/** True while the answer still belongs to the question on screen; releases the block. */
function finishOnboardingAnswer(
  conversationId: string,
  { token, userId }: { token: object; userId?: string }
) {
  if (pendingAnswers.get(conversationId) !== token) return false;
  cancelPendingOnboardingAnswer(conversationId);
  return (
    useChatStore.getState().activeConversationId === conversationId &&
    useCommonStore.getState().user?.id === userId
  );
}

function salutationChosen(
  conversationId: string,
  choice: Pick<OnboardingProgress, 'salutation' | 'salutationText'>,
  setStep: SetOnboardingStep
) {
  const answers: OnboardingAnswers = {
    salutation: choice.salutation,
    salutationText: choice.salutation === 'custom' ? choice.salutationText : undefined,
    salutationInferred: false,
  };
  // The catch-up writes nothing until it completes, so [Später] leaves the preference untouched.
  if (!readProgress().catchUp) void saveOnboardingPreferences(salutationPreferences(answers));
  setStep(ONBOARDING_INDUSTRY_STEP, answers);
  streamOnboardingAssistantReply(
    conversationId,
    industryQuestionFor({ ...readProgress(), ...answers })
  );
}

function industryChosen(
  conversationId: string,
  industry: Pick<OnboardingProgress, 'industry' | 'industryText'>,
  setStep: SetOnboardingStep
) {
  const progress = { ...readProgress(), ...industry };
  const answers: OnboardingAnswers = {
    industry: industry.industry,
    industryText: industry.industry === 'other' ? industry.industryText : undefined,
  };
  if (progress.catchUp) {
    // The catch-up asks nothing more: it ends at the completion card.
    setStep(ONBOARDING_TOOLS_STEP, { ...answers, onboardingCompleted: true });
    void completeOnboardingPreferences({ ...progress, ...answers });
    return;
  }
  void saveOnboardingPreferences(industryPreferences(answers));
  setStep(ONBOARDING_AREA_STEP, answers);
  streamOnboardingAssistantReply(
    conversationId,
    onboardingTeamQuestion(
      onboardingIndustryLabel(answers.industry, answers.industryText),
      progress.addressForm
    )
  );
}

/** Catch-up chips [Gerne du] / [Lieber Sie]. */
export function chooseOnboardingAddressForm(
  conversationId: string,
  form: AddressForm,
  setStep: SetOnboardingStep
) {
  const option = ONBOARDING_ADDRESS_OPTIONS.find((item) => item.value === form);
  const pending =
    option &&
    beginOnboardingAnswer(conversationId, t(option.key, { defaultValue: option.defaultValue }));
  if (!pending || !finishOnboardingAnswer(conversationId, pending)) return;
  // Saved with the industry when the catch-up completes; [Später] writes nothing (C9).
  const progress = { ...readProgress(), addressForm: form };
  if (needsSalutationChoice(progress)) {
    setStep(ONBOARDING_SALUTATION_STEP, { addressForm: form });
    streamOnboardingAssistantReply(conversationId, onboardingSalutationQuestion());
    return;
  }
  setStep(ONBOARDING_INDUSTRY_STEP, { addressForm: form });
  streamOnboardingAssistantReply(conversationId, industryQuestionFor(progress));
}

/** Salutation chips [Frau X] / [Herr X] / [Ohne Anrede]. */
export function chooseOnboardingSalutation(
  conversationId: string,
  salutation: 'frau' | 'herr' | 'none',
  setStep: SetOnboardingStep
) {
  const option = ONBOARDING_SALUTATION_OPTIONS.find((item) => item.value === salutation);
  const label =
    option &&
    t(option.key, { defaultValue: option.defaultValue, familyName: readProgress().familyName });
  const pending = label && beginOnboardingAnswer(conversationId, label);
  if (!pending || !finishOnboardingAnswer(conversationId, pending)) return;
  salutationChosen(conversationId, { salutation }, setStep);
}

/** [Anrede ändern]: the inferred salutation is asked again; the next line uses the choice. */
export function reopenOnboardingSalutation(conversationId: string, setStep: SetOnboardingStep) {
  if (pendingAnswers.has(conversationId)) return;
  setStep(ONBOARDING_SALUTATION_STEP, {
    salutation: undefined,
    salutationText: undefined,
    salutationInferred: false,
  });
  streamOnboardingAssistantReply(conversationId, onboardingSalutationQuestion());
}

/** Industry chips: the chip is the answer, so no extraction runs. */
export function chooseOnboardingIndustry(
  conversationId: string,
  code: Exclude<IndustryCode, 'other'>,
  setStep: SetOnboardingStep
) {
  const pending = beginOnboardingAnswer(conversationId, onboardingIndustryLabel(code));
  if (!pending || !finishOnboardingAnswer(conversationId, pending)) return;
  industryChosen(conversationId, { industry: code }, setStep);
}

/** A typed industry answer is a sentence the model shortens; the 80-character limit applies to
 *  the stored `other` text, which the extraction validator checks. */
const INDUSTRY_ANSWER_MAX_LENGTH = 300;

type PendingAnswer = NonNullable<ReturnType<typeof beginOnboardingAnswer>>;
type ExtractedAnswer = Awaited<ReturnType<typeof extractOnboardingAnswer>>;

/** Steps 1–4 take typed answers; a finished journey (or finished catch-up) takes none. */
const acceptsTypedAnswer = (
  step: number,
  completed: boolean | undefined,
  progress: OnboardingProgress
) => {
  if (step < ONBOARDING_SALUTATION_STEP || step > ONBOARDING_TOOLS_STEP) return false;
  // A finished catch-up has nothing left to answer, even when the old run kept no tools.
  const isFinished = completed || Boolean(progress.catchUp && progress.industry);
  return !(step === ONBOARDING_TOOLS_STEP && isFinished);
};

/** A typed salutation is parsed locally; the catch-up's du/Sie question takes chips only. */
function answerSalutation(
  conversationId: string,
  text: string,
  progress: OnboardingProgress,
  repeat: (feedback: string, question?: string) => void,
  setStep: SetOnboardingStep
) {
  const form = progress.addressForm;
  if (progress.catchUp && !form) {
    repeat(onboardingAnswerUnrelated(form));
    return;
  }
  const choice = parseSalutationText(text, progress.familyName);
  if (!choice) {
    repeat(onboardingAnswerUnrelated(form), onboardingSalutationCustomPrompt());
    return;
  }
  salutationChosen(conversationId, choice, setStep);
}

/** Runs the extraction for the industry, area or tools step. `failed` is a technical failure. */
async function extractStepAnswer(
  step: number,
  text: string
): Promise<{ value: ExtractedAnswer; failed: boolean }> {
  const language = onboardingLanguage();
  try {
    if (step === ONBOARDING_INDUSTRY_STEP) {
      const labels = optionLabels(ONBOARDING_INDUSTRY_OPTIONS);
      return {
        value: await extractOnboardingAnswer('industry', text, language, labels),
        failed: false,
      };
    }
    const field = step === ONBOARDING_AREA_STEP ? 'team' : 'tools';
    const options = field === 'team' ? ONBOARDING_TEAM_OPTIONS : ONBOARDING_TOOL_OPTIONS;
    return {
      value: await extractOnboardingAnswer(field, text, language, optionLabels(options)),
      failed: false,
    };
  } catch {
    return { value: null, failed: true };
  }
}

/** Saves a valid extracted answer and moves on to the next question. */
function applyExtractedAnswer(
  conversationId: string,
  step: number,
  value: NonNullable<ExtractedAnswer>,
  form: AddressForm | undefined,
  setStep: SetOnboardingStep
) {
  if (typeof value === 'object' && 'code' in value) {
    industryChosen(conversationId, { industry: value.code, industryText: value.text }, setStep);
    return;
  }
  if (typeof value !== 'string') return;

  if (step === ONBOARDING_AREA_STEP) {
    void saveOnboardingPreferences({ workArea: value });
    setStep(ONBOARDING_TOOLS_STEP, {
      onboardingTeam: value,
      onboardingTools: '',
      onboardingCompleted: false,
    });
    streamOnboardingAssistantReply(conversationId, onboardingToolsQuestion(value, form));
    return;
  }
  setStep(ONBOARDING_TOOLS_STEP, { onboardingCompleted: true, onboardingTools: value });
  void completeOnboardingPreferences({ ...readProgress(), tools: value, completed: true });
  streamOnboardingAssistantReply(conversationId, onboardingToolsAcknowledged(value, form));
}

/** Extracts a typed industry, area or tools answer and applies it; repeats the question when it
 *  doesn't fit. Resolves `'retry'` after a technical failure. */
async function answerExtractedStep(
  conversationId: string,
  step: number,
  text: string,
  pending: PendingAnswer,
  progress: OnboardingProgress,
  repeat: (feedback: string, question?: string) => void,
  setStep: SetOnboardingStep
): Promise<'retry' | undefined> {
  const form = progress.addressForm;
  // Text the preference would refuse is never sent anywhere (C11).
  if (
    step === ONBOARDING_INDUSTRY_STEP &&
    !isAcceptedPreferenceText(text, INDUSTRY_ANSWER_MAX_LENGTH)
  ) {
    if (!finishOnboardingAnswer(conversationId, pending)) return;
    repeat(onboardingAnswerUnrelated(form), onboardingIndustryCustomPrompt(form));
    return;
  }

  const { value, failed } = await extractStepAnswer(step, text);
  if (!finishOnboardingAnswer(conversationId, pending)) return;
  if (value) {
    applyExtractedAnswer(conversationId, step, value, form, setStep);
    return;
  }
  if (failed) {
    repeat(onboardingAnswerRetry(form));
    // A technical failure hands the typed text back to the composer (C4).
    return 'retry';
  }
  repeat(onboardingAnswerUnrelated(form));
}

/** Chips and composer answers share the same extraction and validation gate. */
export async function answerOnboardingPrompt(
  conversationId: string,
  step: number,
  completed: boolean | undefined,
  text: string,
  setStep: SetOnboardingStep
): Promise<'retry' | undefined> {
  const trimmed = text.trim();
  const progress = readProgress();
  if (!acceptsTypedAnswer(step, completed, progress)) return;
  const pending = beginOnboardingAnswer(conversationId, trimmed);
  if (!pending) return;

  const repeat = (feedback: string, question = onboardingQuestionFor(step, progress)) =>
    streamOnboardingAssistantReply(conversationId, `${feedback}\n\n${question}`);

  if (step === ONBOARDING_SALUTATION_STEP) {
    if (finishOnboardingAnswer(conversationId, pending)) {
      answerSalutation(conversationId, trimmed, progress, repeat, setStep);
    }
    return;
  }
  return answerExtractedStep(conversationId, step, trimmed, pending, progress, repeat, setStep);
}
